// The rule of a public campaign in the dashboard's words (spec 2026-10-21
// §2.6, handoff §2–§4): one shape for the three types, the sentence, name
// and code the New campaign modal writes from it, the code's format, and
// the checks both modals run. The api's field names live in
// campaign-body.js. The free item's Store choice (D3) lives here too,
// because it follows the rule's product.

export const RULE_TYPES = { discount: 1, free_item: 0, buy_x_get_y: 2 };

// Offer line 2 on the list and the Edit modal's subtitle (handoff §6, §7).
export const TYPE_LABELS = {
  discount: 'Spend & get discount',
  free_item: 'Spend & get free item',
  buy_x_get_y: 'Buy X get Y',
};

export const typeOfApi = (apiType) =>
  Object.keys(RULE_TYPES).find((key) => RULE_TYPES[key] === apiType) || null;

// A fresh rule per type. Buy X get Y starts at the handoff's 1 + 1; the
// money fields start empty, so Continue waits for the restaurant's numbers.
export const emptyRule = (type) => ({
  type,
  minSpend: null,
  amount: null,
  unit: 'percent',
  productId: null,
  itemId: null,
  buyQty: type === 'buy_x_get_y' ? 1 : null,
  getQty: type === 'buy_x_get_y' ? 1 : null,
});

export const CODE_MAX = 12;

// The discount and code sentences are the plan's verbatim copy; the
// minimum-spend, product, option, quantity and name lines are new strings
// the designer brief confirms.
export const MESSAGES = {
  minSpend: 'Minimum spend must be more than 0.',
  amountPositive: 'Discount must be more than 0.',
  percentMax: 'A percentage discount can’t be more than 100%.',
  belowMinSpend: 'The discount must be less than the minimum spend.',
  product: 'Choose a product.',
  item: 'Choose an option.',
  quantity: 'Enter 1 or more.',
  name: 'Enter a name.',
  codeEmpty: 'Enter a code.',
  codeChars: 'Use letters and numbers only.',
  codeLength: 'Keep it under 12 characters.',
};

export const CODE_FORMAT_MESSAGES = [MESSAGES.codeEmpty, MESSAGES.codeChars, MESSAGES.codeLength];

const TAKEN_HEAD = 'This code is already used by “';
const TAKEN_TAIL = '”. Choose another one.';

export const takenMessage = (name) => `${TAKEN_HEAD}${name}${TAKEN_TAIL}`;

// The api's duplicate refusal (400, a plain string) starts the same way.
export const isTakenMessage = (text) => typeof text === 'string' && text.startsWith(TAKEN_HEAD);

const finite = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const atLeastOne = (value) => Number.isInteger(value) && value >= 1;

// Money without a trailing .00 (handoff §2): $20, $12.50, $1,000.
export const formatMoney = (value) => {
  const amount = finite(value) ?? 0;
  const cents = Math.round(amount * 100) % 100 !== 0;
  const digits = cents ? 2 : 0;
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

export const formatNumber = (value) =>
  (finite(value) ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

// Handoff §4: a product's items are its "main option"; one item means no
// option to choose.
export const productHasOptions = (product) => (product?.items?.length || 0) > 1;

export const itemOf = (product, itemId) =>
  (product?.items || []).find((item) => item.id === itemId) || null;

// Handoff §2: discount — both > 0, percent ≤ 100, fixed < min spend; free
// item — min spend > 0, a product and (when it has options) an option;
// Buy X get Y — both quantities ≥ 1 and a product.
export const validateRule = (rule, product) => {
  const errors = {};
  if (!rule) return errors;
  const minSpend = finite(rule.minSpend);
  if (rule.type === 'discount' || rule.type === 'free_item') {
    if (minSpend === null || minSpend <= 0) errors.minSpend = MESSAGES.minSpend;
  }
  if (rule.type === 'discount') {
    const amount = finite(rule.amount);
    if (amount === null || amount <= 0) errors.amount = MESSAGES.amountPositive;
    else if (rule.unit === 'percent' && amount > 100) errors.amount = MESSAGES.percentMax;
    else if (rule.unit === 'fixed' && minSpend !== null && minSpend > 0 && amount >= minSpend) {
      errors.amount = MESSAGES.belowMinSpend;
    }
  }
  if (rule.type === 'free_item') {
    if (!rule.productId || !product) errors.productId = MESSAGES.product;
    else if (!itemOf(product, rule.itemId)) errors.itemId = MESSAGES.item;
  }
  if (rule.type === 'buy_x_get_y') {
    if (!rule.productId) errors.productId = MESSAGES.product;
    if (!atLeastOne(rule.buyQty)) errors.buyQty = MESSAGES.quantity;
    if (!atLeastOne(rule.getQty)) errors.getQty = MESSAGES.quantity;
  }
  return errors;
};

export const ruleIsValid = (rule, product) =>
  Boolean(rule) && Object.keys(validateRule(rule, product)).length === 0;

// The fields an edit can change, per type. A free item's product is not
// one: it is read from the item (ProductChoices), and changing the
// product always changes the item.
export const RULE_FIELDS = {
  discount: ['minSpend', 'amount', 'unit'],
  free_item: ['minSpend', 'itemId'],
  buy_x_get_y: ['productId', 'buyQty', 'getQty'],
};

export const changedRuleFields = (initial, rule) => {
  if (!initial || !rule) return [];
  return (RULE_FIELDS[rule.type] || []).filter((field) => (initial[field] ?? null) !== (rule[field] ?? null));
};

// A changed field also re-checks the fields its rule ties it to: a new
// minimum re-checks a fixed amount; a new unit re-checks the amount.
const CHECKED_WITH = {
  minSpend: ['minSpend', 'amount'],
  amount: ['amount'],
  unit: ['amount'],
  itemId: ['productId', 'itemId'],
  productId: ['productId'],
  buyQty: ['buyQty'],
  getQty: ['getQty'],
};

// Spec §2.5 / Review Focus 3: the Edit modal checks only what changed, so
// an old campaign that predates the rules still saves its status or name.
export const validateChangedRule = (initial, rule, product) => {
  const errors = validateRule(rule, product);
  const checked = new Set(changedRuleFields(initial, rule).flatMap((field) => CHECKED_WITH[field] || [field]));
  return Object.fromEntries(Object.entries(errors).filter(([field]) => checked.has(field)));
};

// Handoff §2 "Summary sentence (name) templates".
export const summarySentence = (rule, product, item) => {
  if (!rule) return '';
  if (rule.type === 'discount') {
    const off = rule.unit === 'percent' ? `${formatNumber(rule.amount)}%` : formatMoney(rule.amount);
    return `Spend ${formatMoney(rule.minSpend)}, get ${off} off`;
  }
  if (rule.type === 'free_item') {
    const option = productHasOptions(product) && item ? ` (${item.name})` : '';
    return `Spend ${formatMoney(rule.minSpend)}, get a free ${product?.name || ''}${option}`;
  }
  if (rule.type === 'buy_x_get_y') {
    return `Buy ${rule.buyQty ?? ''} ${product?.name || ''}, get ${rule.getQty ?? ''} free`;
  }
  return '';
};

// A product name as code letters: accents folded, letters and digits only.
export const codeWord = (name) =>
  String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

// The integer part of an amount: the handoff gives no cents example, and
// SPEND12GET2 reads better than SPEND1250GET250. Digits only: a negative
// reads as 0 and a huge one stops at the largest safe integer (no `-`, no
// `1e+21`).
const codeNumber = (value) => String(Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.floor(finite(value) ?? 0))));

// Handoff §3: SPEND20GET10, SPEND20GET5, SPEND10COKE, BUY1FRIES1; the
// product word is cut so the whole code fits 12.
export const generateCode = (rule, product) => {
  if (!rule) return '';
  const word = codeWord(product?.name);
  const fit = (head, tail = '') =>
    `${head}${word.slice(0, Math.max(0, CODE_MAX - head.length - tail.length))}${tail}`.slice(0, CODE_MAX);
  if (rule.type === 'discount') {
    return `SPEND${codeNumber(rule.minSpend)}GET${codeNumber(rule.amount)}`.slice(0, CODE_MAX);
  }
  if (rule.type === 'free_item') return fit(`SPEND${codeNumber(rule.minSpend)}`);
  if (rule.type === 'buy_x_get_y') return fit(`BUY${codeNumber(rule.buyQty)}`, codeNumber(rule.getQty));
  return '';
};

// Spec §1.2's format rule, as the api applies it: trimmed, letters and
// digits only (ASCII), 1–12 characters.
export const codeFormatError = (code) => {
  const value = String(code ?? '').trim();
  if (!value) return MESSAGES.codeEmpty;
  if (!/^[A-Za-z0-9]+$/.test(value)) return MESSAGES.codeChars;
  if (value.length > CODE_MAX) return MESSAGES.codeLength;
  return null;
};

// D3 (spec §2.4): a free item's Store is one that sells its product;
// "All stores" exists only when every store of the company sells it.
// FinDropdown keys its options by value, so All stores is a sentinel that
// the modals turn back into storeid null.
export const ALL_STORES = '__all__';
const ALL_OPTION = { value: ALL_STORES, label: 'All stores' };

export const storeChoices = (type, product, stores = []) => {
  const named = (stores || []).map((store) => ({ value: store.id, label: store.name }));
  if (type !== 'free_item' || !product) return [ALL_OPTION, ...named];
  const selling = new Set(product.storeids || []);
  const sold = named.filter((option) => selling.has(option.value));
  const everyStore = named.length > 0 && sold.length === named.length;
  return everyStore ? [ALL_OPTION, ...sold] : sold;
};

// Keeps the current store while it is offered; else the first store
// offered (the first selling store), or All stores (null).
export const pickStore = (choices, current) => {
  const wanted = current ?? ALL_STORES;
  if (choices.some((option) => option.value === wanted)) return current ?? null;
  const first = choices[0];
  return first && first.value !== ALL_STORES ? first.value : null;
};
